/**
 * Layout Migrations Module
 *
 * This module handles version upgrades for dashboard layouts.
 * Each upgrade function converts from one version to the next.
 *
 * Version History:
 * - V1: Grid-based layout with x, y, w, h coordinates (vue3-grid-layout)
 * - V2: Row/cell-based layout with percentage widths (future)
 */

import omit from 'lodash/omit';

import constants from '@/app/dashboards/constants';

import {
  type AnyLayout,
  LAYOUT_VERSIONS,
  type LayoutItem,
  type LayoutV2,
  type LayoutV2Cell,
  type LayoutV2Row,
  type LayoutVersion,
} from './layout.types';

// ============================================================================
// Version Constants
// ============================================================================

export const LATEST_VERSION: LayoutVersion = LAYOUT_VERSIONS.V2;

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if layout is a V1 layout (array format)
 */
export function isV1Layout(layout: AnyLayout | null | undefined): layout is LayoutItem[] {
  return Array.isArray(layout);
}

/**
 * Type guard to check if layout is a V2+ layout (object with version)
 */
export function isV2Layout(layout: AnyLayout | null | undefined): layout is LayoutV2 {
  return (
    layout !== null &&
    layout !== undefined &&
    !Array.isArray(layout) &&
    typeof layout === 'object' &&
    'version' in layout
  );
}

// ============================================================================
// Version Detection
// ============================================================================

/**
 * Get the version of a layout
 * @param layout - The layout to check (V1 array or V2+ object)
 * @returns Version number (1, 2, etc.) or null if invalid
 */
export function getLayoutVersion(layout: AnyLayout | null | undefined): number | null {
  if (!layout) {
    return null;
  }

  // V2+ layouts are objects with a version property
  if (isV2Layout(layout)) {
    return Number(layout.version);
  }

  // V1 layouts are arrays
  if (isV1Layout(layout)) {
    return LAYOUT_VERSIONS.V1;
  }

  return null;
}

/**
 * Check if a layout is a specific version
 * @param layout - The layout to check
 * @param version - The version to check for
 * @returns True if layout matches the specified version
 */
export function isLayoutVersion(
  layout: AnyLayout | null | undefined,
  version: LayoutVersion,
): boolean {
  return getLayoutVersion(layout) === version;
}

// ============================================================================
// V1 → V2 Migration
// ============================================================================

/**
 * Convert grid width (in columns) to percentage
 * @param w - Width in grid columns
 * @param cols - Total number of columns (default 6)
 * @returns Width as percentage (rounded)
 */
function convertWidthToPercentage(
  w: number,
  cols: number = constants.NUMBER_OF_DASHBOARD_COLS,
): number {
  return Math.round((w / cols) * 100);
}

/**
 * Properties to omit when converting V1 layout item to V2 cell meta
 */
const V1_GRID_PROPERTIES = [
  'x',
  'y',
  'w',
  'h',
  'i',
  'moved',
  'minW',
  'minH',
  'maxH',
  'reportId',
  'report_id',
  'type',
] as const;

/**
 * Upgrade a V1 grid-based layout to V2 row/cell-based format
 *
 * V1 Format: Array of { x, y, w, h, type, report_id, ... }
 * V2 Format: { version: '2', rows: [{ cells: [{ type, reportId, width, meta }] }] }
 *
 * @param layout - The V1 layout array with x, y, w, h coordinates
 * @returns V2 layout object with rows and cells, or null if invalid
 */
export function upgradeV1ToV2(layout: LayoutItem[]): LayoutV2 | null {
  if (!layout || !Array.isArray(layout) || layout.length === 0) {
    return null;
  }

  // Get unique y values and sort them
  const uniqueYValues = [...new Set(layout.map((item) => item.y))].sort((a, b) => a - b);

  // Group items by y value
  const rows: LayoutV2Row[] = uniqueYValues.map((y) => {
    // Get all items at this y position, sorted by x
    const itemsAtY = layout.filter((item) => item.y === y).sort((a, b) => a.x - b.x);

    // Convert items to cells
    const cells: LayoutV2Cell[] = itemsAtY.map((item) => {
      // Copy all properties except grid-specific ones
      const meta = omit(item, V1_GRID_PROPERTIES) as Record<string, unknown>;

      return {
        meta,
        type: item.type,
        reportId: item.reportId ?? item.report_id,
        width: convertWidthToPercentage(item.w),
      };
    });

    return { cells };
  });

  return {
    id: 'dashboard-unknown',
    version: '2',
    rows,
  };
}

// ============================================================================
// Main Upgrade Function
// ============================================================================

/**
 * Type for upgrade functions that convert between layout versions
 */
type UpgradeFn = (layout: AnyLayout) => AnyLayout | null;

/**
 * Registry of upgrade functions keyed by "fromVersion-toVersion"
 */
const UPGRADE_FUNCTIONS: Record<string, UpgradeFn> = {
  '1-2': upgradeV1ToV2 as UpgradeFn,
  // Future: '2-3': upgradeV2ToV3,
};

/**
 * Target version type - can be a number or 'latest'
 */
type TargetVersion = number | 'latest';

/**
 * Upgrade a layout to a target version
 *
 * This function handles chained upgrades (e.g., V1 → V2 → V3)
 * by applying each upgrade step sequentially.
 *
 * @param layout - The layout to upgrade
 * @param targetVersion - Target version number or 'latest'
 * @returns Upgraded layout or original if already at target, null if upgrade fails
 */
export function upgradeLayout(
  layout: AnyLayout | null | undefined,
  targetVersion: TargetVersion = 'latest',
): AnyLayout | null {
  const currentVersion = getLayoutVersion(layout);

  if (currentVersion === null || !layout) {
    return null;
  }

  const target = targetVersion === 'latest' ? LATEST_VERSION : Number(targetVersion);

  // Already at target version
  if (currentVersion >= target) {
    return layout;
  }

  // Apply upgrades sequentially
  let upgradedLayout: AnyLayout | null = layout;
  let version = currentVersion;

  while (version < target) {
    const nextVersion = version + 1;
    const upgradeKey = `${version}-${nextVersion}`;
    const upgradeFn = UPGRADE_FUNCTIONS[upgradeKey];

    if (!upgradeFn) {
      // No upgrade path available
      break;
    }

    upgradedLayout = upgradeFn(upgradedLayout!);

    if (!upgradedLayout) {
      // Upgrade failed
      return null;
    }

    version = nextVersion;
  }

  return upgradedLayout;
}

/**
 * Check if a layout can be upgraded to a target version
 * @param layout - The layout to check
 * @param targetVersion - Target version number or 'latest'
 * @returns True if upgrade path exists
 */
export function canUpgradeLayout(
  layout: AnyLayout | null | undefined,
  targetVersion: TargetVersion = 'latest',
): boolean {
  const currentVersion = getLayoutVersion(layout);

  if (currentVersion === null) {
    return false;
  }

  const target = targetVersion === 'latest' ? LATEST_VERSION : Number(targetVersion);

  if (currentVersion >= target) {
    return true; // Already at or above target
  }

  // Check if all upgrade paths exist
  for (let v = currentVersion; v < target; v += 1) {
    const upgradeKey = `${v}-${v + 1}`;
    if (!UPGRADE_FUNCTIONS[upgradeKey]) {
      return false;
    }
  }

  return true;
}
